import React, { useState } from 'react'

import {
  AccountCircle,
  HelpOutline,
  PlayArrow,
  Stop,
  Settings,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material'
import {
  Button,
  Avatar,
  AppBar,
  Toolbar,
  Box,
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
} from '@mui/material'
import {
  lastDayOfMonth,
  setDate,
  differenceInBusinessDays,
  isSameMonth,
  parseISO,
  addDays,
} from 'date-fns'
import { useDispatch } from 'react-redux'

import { toggleShortcuts } from '../../actions/trackerActions'
import Logo from '../../assets/logo-icon.svg'
import { ComponentHeights, ComponentWidths } from '../../consts'
import {
  useSetTutorialEnabled,
  useSetTutorialIndex,
} from '../../hooks/tutorialHooks'
import { useSelector } from '../../hooks/useSelector'
import { createStyles } from '../../styles'
import { formatTime, SECONDS_IN_WORKING_DAY } from '../../utils'
import { SettingsDialog } from '../SettingsDialog'
import { SyncPoints } from '../WorklogsHeader/SyncPoints'

import { Logout } from './Logout'
import { secondsAssignedMonthlySelector } from './secondsAssignedMonthlySelector'
import { generalStyles } from './styles'

const workingDaysCount = (displayedISODate: string) => {
  const displayedDate = parseISO(displayedISODate)
  const endingDate = isSameMonth(new Date(), displayedDate)
    ? new Date()
    : lastDayOfMonth(displayedDate)

  //the function counts with (last, first> not <last, first>
  return differenceInBusinessDays(
    addDays(endingDate, 1),
    setDate(displayedDate, 1)
  )
}

const styles = createStyles({
  header: {
    backgroundColor: (theme) => theme.palette.common.white,
    height: ComponentHeights.HEADER,
    boxShadow: 'none',
    borderBottom: '1px solid',
    borderBottomColor: (theme) => theme.palette.border.main,
    boxSizing: 'border-box',
  },
  container: {
    paddingX: 2,
    paddingY: 0,
  },
  logo: {
    width: 23,
    height: 'auto',
    padding: 1,
    // TODO: temporary
    marginRight: 2,
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  name: {
    marginRight: 1,
  },
  btn: {
    height: 32,
  },
  tutorial: {
    marginRight: 2,
    display: 'flex',
    alignItems: 'center',
    color: (theme) => theme.palette.common.white,
    backgroundColor: (theme) => theme.palette.button.lightgrey,
    paddingLeft: '5px',
    paddingRight: 1,
    '&:hover': {
      backgroundColor: (theme) => theme.palette.button.darkgrey,
    },
    '& > span > svg': {
      width: 16,
      height: 16,
    },
  },
  btnTitle: {
    textTransform: 'none',
    fontSize: 12,
    lineHeight: '12px',
  },
  tutorialTitle: {
    marginLeft: '6px',
  },
  shortcutsButton: {
    marginRight: 2,
    color: (theme) => theme.palette.button.darkgrey,
  },
  shortcutsTitle: {
    marginLeft: 1,
    lineHeight: '23px',
  },
  avatarButton: {
    color: (theme) => theme.palette.button.darkgrey,
  },
  avatar: {
    marginRight: 1,
  },
  menu: {
    width: ComponentWidths.MENU_DROPDOWN,
    boxSizing: 'border-box',
    padding: 1,
  },
  menuItem: {
    ...generalStyles.menuItem,
    borderBottom: '1px solid',
    borderBottomColor: (theme) => theme.palette.border.main,
  },
  menuText: generalStyles.menuText,
  requiredTime: {
    borderRadius: '3px',
    padding: '6px',
    fontSize: '11px',
    textAlign: 'center',
    marginRight: 3,
    border: `1px solid`,
  },
})

export const Header = () => {
  const dispatch = useDispatch()
  const tutorialEnabled = useSelector((state) => state.tutorial.enabled)
  const tutorialInDropdown = useSelector((state) => state.tutorialInDropdown)
  const setTutorialEnabled = useSetTutorialEnabled()
  const setTutorialIndex = useSetTutorialIndex()
  const user = useSelector((state) => state.user)
  const shortcutsShown = useSelector((state) => state.shortcutsShown)
  const ftePercentage = useSelector((state) => state.settings.ftePercentage)
  const secondsAssignedMonthly = useSelector(secondsAssignedMonthlySelector)
  const displayedDate = useSelector((state) => state.displayedDate)

  const currentPeriodSeconds =
    secondsAssignedMonthly -
    (workingDaysCount(displayedDate) * SECONDS_IN_WORKING_DAY * ftePercentage) /
      100

  const startTutorial = () => {
    handleClose()
    setTutorialIndex(0)
    setTutorialEnabled(!tutorialEnabled)
  }

  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [isSettingsOpened, setSettingsOpened] = useState(false)

  const openSettings = () => {
    setAnchorEl(null)
    setSettingsOpened(true)
  }
  const handleClick = (event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'logout' : undefined

  return !user ? (
    <AppBar color="transparent" position="static" sx={styles.header} />
  ) : (
    <AppBar color="transparent" position="static" sx={styles.header}>
      <Toolbar sx={styles.container}>
        <Box
          component="img"
          src={Logo}
          alt="logo"
          className="tutorial-welcome"
          sx={styles.logo}
        />

        <Box sx={styles.right}>
          <SyncPoints />
          <Box sx={styles.requiredTime}>
            {`Current Period ${currentPeriodSeconds > 0 ? '+' : ''}${formatTime(
              currentPeriodSeconds
            )}`}
          </Box>
          {(!tutorialInDropdown || tutorialEnabled) && (
            <Button
              sx={[styles.btn, styles.tutorial]}
              onClick={startTutorial}
              data-cy="tutorial-button"
            >
              {tutorialEnabled ? (
                <Stop fontSize="small" />
              ) : (
                <PlayArrow fontSize="small" />
              )}
              <Box sx={[styles.btnTitle, styles.tutorialTitle]}>
                {tutorialEnabled ? 'Stop' : 'Start'} tutorial
              </Box>
            </Button>
          )}
          <Tooltip title={shortcutsShown ? 'Hide shortcuts' : 'Show shortcuts'}>
            <Button
              onClick={() => dispatch(toggleShortcuts())}
              className="tutorial-shortcuts"
              sx={styles.shortcutsButton}
            >
              <HelpOutline />
              <Box sx={styles.shortcutsTitle}>shortcuts</Box>
            </Button>
          </Tooltip>

          <Button
            aria-describedby={id}
            onClick={handleClick}
            sx={styles.avatarButton}
          >
            {user.avatarUrl ? (
              <Avatar alt={user.name} src={user.avatarUrl} sx={styles.avatar} />
            ) : (
              <Avatar sx={styles.avatar}>
                <AccountCircle />
              </Avatar>
            )}
            {anchorEl === null ? (
              <ExpandMore fontSize="small" />
            ) : (
              <ExpandLess fontSize="small" />
            )}
          </Button>

          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <MenuList sx={styles.menu}>
              <MenuItem onClick={openSettings} sx={styles.menuItem}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText sx={styles.menuText} primary="Settings" />
              </MenuItem>
              {tutorialInDropdown && (
                <MenuItem onClick={startTutorial} sx={styles.menuItem}>
                  <ListItemIcon>
                    <PlayArrow fontSize="small" />
                  </ListItemIcon>
                  <ListItemText sx={styles.menuText} primary="Start tutorial" />
                </MenuItem>
              )}
              <Logout />
            </MenuList>
          </Popover>
          <SettingsDialog
            isOpened={isSettingsOpened}
            onClose={() => setSettingsOpened(false)}
          />
        </Box>
      </Toolbar>
    </AppBar>
  )
}
